import type { OrderBy } from '@/lib/graphql-query-builder';

/**
 * Types shared by the five service desk sub-modules (NOC, BYOD, Business Card, ID Card, MetLife).
 *
 * Each sub-module owns its own Blocks schema (`NocApplication`, `ByodApplication`, ...) that mirrors
 * the legacy .NET entity 1:1 -- the fields below are the ones every one of those entities shares.
 */

export interface ApplicationBase {
  ItemId: string;
  CreatedDate?: string;
  UserId: string;
  Designation?: string;
  Status: string;
  /** When the application was submitted (UTC instant). */
  AppliedDateUtc?: string;
  ApprovedBy?: string;
  ApprovedByName?: string;
  /** When the application was approved or rejected (UTC instant). */
  ApprovedDateUtc?: string;
  ApprovalStatusReason?: string;
  DepartmentId?: string;
  /** IANA zone of the organization the application was made in; its moments are shown in it. */
  Timezone?: string;
}

export interface PagingResult<T> {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: T[];
}

export interface ListParams {
  pageNo: number;
  pageSize: number;
  /** Mongo-style filter object; shaped into a `where` GraphQL literal by the API layer. */
  filter?: Record<string, unknown>;
  /** Defaults to newest application first (`DEFAULT_APPLICATIONS_ORDER`). */
  order?: OrderBy[];
}

/** Filters common to every "requests" (admin) list. */
export interface RequestFilters {
  keyword: string;
  departmentId: string;
  status: string;
}

export const EMPTY_REQUEST_FILTERS: RequestFilters = {
  keyword: '',
  departmentId: '',
  status: '',
};

/** Facts about the signed-in employee that pre-fill forms and are checked on new applications. */
export interface EmployeeSnapshot {
  userId: string;
  displayName: string;
  email: string;
  phoneNumber: string;
  employeeId: string;
  designation: string;
  departmentId: string;
  sex: string;
  bloodGroup: string;
  passportNo: string;
  joiningDate: string;
  /** Current-HR-year employment type (e.g. `Probation`, `Internship`, `Permanent`). */
  employmentType: string;
}

/** A business-rule failure; `message` is a translation key the global error handler translates. */
export class ServiceDeskRuleError extends Error {
  constructor(translationKey: string) {
    super(translationKey);
    this.name = 'ServiceDeskRuleError';
  }
}
